/**
 * CRM-1 seed data — 3 accounts, 5 contacts, 4 deals, sample activities.
 *
 * Fixtures are named (no generated IDs) so CRM-6 prospect-mirror seed and
 * CRM-9 sales-brief seed can reference them by stable id.
 */
import { DEMO_TENANT_ID } from './tenantDefaults';

const T = DEMO_TENANT_ID;
const iso = (days = 0) => new Date(Date.now() - days * 86400000).toISOString();

// ── Accounts ─────────────────────────────────────────────────────────────
export const SEED_CRM_ACCOUNTS = [
  {
    id: 'acct-keyston',
    tenantId: T,
    name: 'Keyston Bros',
    website: 'keyston.com',
    industry: 'Specialty Distribution — Automotive Upholstery & Flooring',
    hq: { city: 'Pomona', state: 'CA' },
    employees: 340,
    annualRevenue: 220_000_000,
    painProfiles: ['specialty_distributor'],
    primaryModule: 'IMS',
    estimatedFreightSpend: 18_400_000,
    pipelineStage: 'negotiation',
    accountOwner: 'rep-demo-001',
    tags: ['keyston-demo', 'ltl-heavy'],
    activeLoadsCount: 6,
    healthScore: 78,
    notes: 'LTL-heavy specialty distributor. Demo-ready via DOC-4 Keyston LTL automation.',
    createdAt: iso(120),
  },
  {
    id: 'acct-riverside',
    tenantId: T,
    name: 'Riverside Natural Foods',
    website: 'riversidenaturalfoods.com',
    industry: 'Frozen Food Manufacturer — Granola & Snack Bars',
    hq: { city: 'Vaughan', state: 'ON' },
    employees: 420,
    annualRevenue: 185_000_000,
    painProfiles: ['frozen_food_cold_chain'],
    primaryModule: 'IoT',
    estimatedFreightSpend: 12_100_000,
    pipelineStage: 'proposal',
    accountOwner: 'rep-demo-001',
    tags: ['riverside-demo', 'cold-chain'],
    activeLoadsCount: 4,
    healthScore: 82,
    notes: 'Cold-chain manufacturer. Demo-ready via DOC-3 dock-to-broker automation.',
    createdAt: iso(85),
  },
  {
    id: 'acct-midwest',
    tenantId: T,
    name: 'Midwest Grocers',
    website: 'midwestgrocers.com',
    industry: 'Regional Grocery Retail — Private Fleet',
    hq: { city: 'Kansas City', state: 'MO' },
    employees: 2_800,
    annualRevenue: 940_000_000,
    painProfiles: ['retail_replenishment'],
    primaryModule: 'Fleet',
    estimatedFreightSpend: 58_600_000,
    pipelineStage: 'discovery',
    accountOwner: 'rep-demo-002',
    tags: ['private-fleet', 'backhaul'],
    activeLoadsCount: 0,
    healthScore: 64,
    notes: 'Private-fleet retailer with empty backhaul problem. Backhaul lane-match demo in motion.',
    createdAt: iso(32),
    // Churn-risk fixtures (CRM-8) — CRITICAL account
    lastPortalLoginAt: iso(42),
    keyContactDeparted: true,
    latePayment: true,
    openDisputes: 2,
    slaBreachRate30d: 0.14,
    supportEscalations30d: 3,
  },
  {
    id: 'acct-sunbelt-churn',
    tenantId: T,
    name: 'Sunbelt Produce',
    website: 'sunbeltproduce.com',
    industry: 'Produce Distribution',
    hq: { city: 'Fresno', state: 'CA' },
    employees: 520,
    annualRevenue: 190_000_000,
    painProfiles: ['frozen_food_cold_chain'],
    primaryModule: 'IoT',
    estimatedFreightSpend: 11_500_000,
    pipelineStage: 'closed_won',
    accountOwner: 'rep-demo-001',
    tags: ['customer', 'cold-chain'],
    activeLoadsCount: 3,
    healthScore: 69,
    notes: 'Active customer — SLA dips + contract renewal in 48d. HIGH risk on daily scan.',
    createdAt: iso(320),
    lastPortalLoginAt: iso(20),
    slaBreachRate30d: 0.12,
  },
  {
    id: 'acct-apex-hauling',
    tenantId: T,
    name: 'Apex Hauling Solutions',
    website: 'apexhauling.com',
    industry: '3PL / Brokerage',
    hq: { city: 'Dallas', state: 'TX' },
    employees: 160,
    annualRevenue: 48_000_000,
    painProfiles: ['third_party_broker'],
    primaryModule: 'TMS',
    estimatedFreightSpend: 12_000_000,
    pipelineStage: 'closed_won',
    accountOwner: 'rep-demo-002',
    tags: ['customer'],
    activeLoadsCount: 8,
    healthScore: 75,
    notes: 'Active customer — mild portal disengagement, flagged MEDIUM on daily scan.',
    createdAt: iso(240),
    lastPortalLoginAt: iso(16),
  },
];

// ── Contacts ─────────────────────────────────────────────────────────────
export const SEED_CRM_CONTACTS = [
  {
    id: 'contact-tommy-duncan',
    tenantId: T,
    accountId: 'acct-keyston',
    accountName: 'Keyston Bros',
    firstName: 'Tommy',
    lastName: 'Duncan',
    fullName: 'Tommy Duncan',
    title: 'VP Supply Chain',
    email: 'tduncan@keyston.com',
    phone: '+1-909-555-0142',
    linkedinUrl: 'linkedin.com/in/tommyduncan',
    painProfiles: ['specialty_distributor'],
    primaryModule: 'IMS',
    seniority: 'vp',
    decisionMakerRole: 'economic_buyer',
    timezone: 'America/Los_Angeles',
    lastActivityAt: iso(3),
    leadScore: 82,
    tags: ['keyston-demo', 'champion'],
    notes: 'Champion from DOC-4 Keyston LTL walkthrough. Focus: class/NMFC miscode leakage.',
    createdAt: iso(62),
  },
  {
    id: 'contact-andrew-kahn',
    tenantId: T,
    accountId: 'acct-keyston',
    accountName: 'Keyston Bros',
    firstName: 'Andrew',
    lastName: 'Kahn',
    fullName: 'Andrew Kahn',
    title: 'Director of Logistics',
    email: 'akahn@keyston.com',
    phone: '+1-909-555-0178',
    linkedinUrl: 'linkedin.com/in/andrewkahn',
    painProfiles: ['specialty_distributor'],
    primaryModule: 'IMS',
    seniority: 'director',
    decisionMakerRole: 'technical_evaluator',
    timezone: 'America/Los_Angeles',
    lastActivityAt: iso(1),
    leadScore: 76,
    tags: ['keyston-demo', 'technical-evaluator'],
    notes: 'Andrew owns the LTL bid-board and dispatches with three brokers today.',
    createdAt: iso(62),
  },
  {
    id: 'contact-michelle-bero',
    tenantId: T,
    accountId: 'acct-riverside',
    accountName: 'Riverside Natural Foods',
    firstName: 'Michelle',
    lastName: 'Bero',
    fullName: 'Michelle Bero',
    title: 'Supply Chain Manager',
    email: 'mbero@riversidenaturalfoods.com',
    phone: '+1-905-555-0198',
    linkedinUrl: 'linkedin.com/in/michellebero',
    painProfiles: ['frozen_food_cold_chain'],
    primaryModule: 'IoT',
    seniority: 'manager',
    decisionMakerRole: 'champion',
    timezone: 'America/Toronto',
    lastActivityAt: iso(2),
    leadScore: 79,
    tags: ['riverside-demo', 'cold-chain', 'champion'],
    notes: 'Lives in reefer temp-breach claim disputes. Riverside DOC-3 champion.',
    createdAt: iso(48),
  },
  {
    id: 'contact-daniel-torres',
    tenantId: T,
    accountId: 'acct-midwest',
    accountName: 'Midwest Grocers',
    firstName: 'Daniel',
    lastName: 'Torres',
    fullName: 'Daniel Torres',
    title: 'Logistics Director',
    email: 'dtorres@midwestgrocers.com',
    phone: '+1-816-555-0215',
    linkedinUrl: 'linkedin.com/in/danieltorres',
    painProfiles: ['retail_replenishment'],
    primaryModule: 'Fleet',
    seniority: 'director',
    decisionMakerRole: 'economic_buyer',
    timezone: 'America/Chicago',
    lastActivityAt: iso(6),
    leadScore: 58,
    tags: ['private-fleet', 'backhaul'],
    notes: 'Runs 240-truck private fleet. Needs backhaul lane-matching to cover driver pay raise.',
    createdAt: iso(25),
  },
  {
    id: 'contact-jessica-park',
    tenantId: T,
    accountId: 'acct-midwest',
    accountName: 'Midwest Grocers',
    firstName: 'Jessica',
    lastName: 'Park',
    fullName: 'Jessica Park',
    title: 'VP Operations',
    email: 'jpark@midwestgrocers.com',
    phone: '+1-816-555-0221',
    linkedinUrl: 'linkedin.com/in/jessicapark-ops',
    painProfiles: ['retail_replenishment'],
    primaryModule: 'Fleet',
    seniority: 'vp',
    decisionMakerRole: 'economic_buyer',
    timezone: 'America/Chicago',
    lastActivityAt: iso(10),
    leadScore: 51,
    tags: ['private-fleet'],
    notes: 'Executive sponsor — needs ROI case before expanding pilot beyond KC DC.',
    createdAt: iso(25),
  },
];

// ── Deals / Pipeline ─────────────────────────────────────────────────────
export const SEED_CRM_DEALS = [
  {
    id: 'deal-keyston-ltl',
    tenantId: T,
    accountId: 'acct-keyston',
    accountName: 'Keyston Bros',
    contactId: 'contact-tommy-duncan',
    name: 'Keyston Bros — LTL Booking & Class Audit',
    stage: 'negotiation',
    module: 'IMS',
    amount: 185_000,
    probability: 0.55,
    expectedCloseDate: iso(-28),
    owner: 'rep-demo-001',
    lastActivityAt: iso(2),
    nextStep: 'Send mutual-close plan w/ procurement',
    createdAt: iso(55),
  },
  {
    id: 'deal-riverside-coldchain',
    tenantId: T,
    accountId: 'acct-riverside',
    accountName: 'Riverside Natural Foods',
    contactId: 'contact-michelle-bero',
    name: 'Riverside NF — Cold-Chain + Dock-to-Broker',
    stage: 'proposal',
    module: 'IoT',
    amount: 142_000,
    probability: 0.45,
    expectedCloseDate: iso(-45),
    owner: 'rep-demo-001',
    lastActivityAt: iso(4),
    nextStep: 'Proposal review with IT + QA',
    createdAt: iso(40),
  },
  {
    id: 'deal-midwest-backhaul',
    tenantId: T,
    accountId: 'acct-midwest',
    accountName: 'Midwest Grocers',
    contactId: 'contact-daniel-torres',
    name: 'Midwest Grocers — Private-Fleet Backhaul',
    stage: 'discovery',
    module: 'Fleet',
    amount: 320_000,
    probability: 0.20,
    expectedCloseDate: iso(-90),
    owner: 'rep-demo-002',
    lastActivityAt: iso(7),
    nextStep: 'Technical deep-dive with operations team',
    createdAt: iso(22),
  },
  {
    id: 'deal-midwest-ops',
    tenantId: T,
    accountId: 'acct-midwest',
    accountName: 'Midwest Grocers',
    contactId: 'contact-jessica-park',
    name: 'Midwest Grocers — Ops Visibility Expansion',
    stage: 'qualified',
    module: 'TMS',
    amount: 95_000,
    probability: 0.30,
    expectedCloseDate: iso(-120),
    owner: 'rep-demo-002',
    lastActivityAt: iso(12),
    nextStep: 'Waiting on exec-sponsor ROI review',
    createdAt: iso(18),
  },
];

// ── Activities (email, call, meeting, note) ──────────────────────────────
export const SEED_CRM_ACTIVITIES = [
  {
    id: 'act-keyston-001', tenantId: T, accountId: 'acct-keyston', contactId: 'contact-tommy-duncan', dealId: 'deal-keyston-ltl',
    type: 'email', direction: 'outbound', channel: 'email', subject: 'Keyston LTL — class/NMFC miscode audit recap',
    body: 'Tommy — attached the class-miscode audit on your last 400 LTL tenders. Flagged $38K/quarter in leakage. Ready for the 20-min walkthrough when you are.',
    owner: 'rep-demo-001', outcome: 'replied', at: iso(2),
  },
  {
    id: 'act-keyston-002', tenantId: T, accountId: 'acct-keyston', contactId: 'contact-andrew-kahn', dealId: 'deal-keyston-ltl',
    type: 'call', direction: 'outbound', channel: 'phone', subject: 'Discovery — current broker mix',
    body: 'Andrew walked through broker routing. Confirmed 3 primaries: STG, Saia, XPO. Pain is class coding disputes.',
    owner: 'rep-demo-001', outcome: 'connected', at: iso(5),
  },
  {
    id: 'act-riverside-001', tenantId: T, accountId: 'acct-riverside', contactId: 'contact-michelle-bero', dealId: 'deal-riverside-coldchain',
    type: 'meeting', direction: 'outbound', channel: 'zoom', subject: 'Riverside — reefer breach dispute walkthrough',
    body: 'Showed the dock-to-broker live feed + chain-of-custody export. Michelle asked about QA integration next.',
    owner: 'rep-demo-001', outcome: 'positive', at: iso(4),
  },
  {
    id: 'act-midwest-001', tenantId: T, accountId: 'acct-midwest', contactId: 'contact-daniel-torres', dealId: 'deal-midwest-backhaul',
    type: 'email', direction: 'inbound', channel: 'email', subject: 'Re: Backhaul lane-match pilot',
    body: 'Daniel replied asking for a sample lane-match output against their KC → Chicago returns.',
    owner: 'rep-demo-002', outcome: 'replied', at: iso(6),
  },
  {
    id: 'act-midwest-002', tenantId: T, accountId: 'acct-midwest', contactId: 'contact-jessica-park', dealId: 'deal-midwest-ops',
    type: 'note', direction: 'internal', channel: 'note', subject: 'Exec-sponsor readiness',
    body: 'Jessica wants unit-economics case per DC before expanding pilot. Packaging that this week.',
    owner: 'rep-demo-002', outcome: null, at: iso(9),
  },
];

// Closed-won / closed-lost deals seed CRM-7 win/loss + forecast screens.
SEED_CRM_DEALS.push(
  {
    id: 'deal-closed-1', tenantId: T, accountId: 'acct-keyston', accountName: 'Keyston Bros', contactId: 'contact-tommy-duncan',
    name: 'Keyston Bros — 2026 pilot (Pomona)', stage: 'closed_won', module: 'IMS',
    amount: 88_000, probability: 1.0, expectedCloseDate: iso(30), owner: 'rep-demo-001',
    closeReason: 'operational_proof_point', competitor: 'legacy_tms',
    createdAt: iso(110),
  },
  {
    id: 'deal-closed-2', tenantId: T, accountId: 'acct-riverside', accountName: 'Riverside Natural Foods', contactId: 'contact-michelle-bero',
    name: 'Riverside — Q4 pilot', stage: 'closed_won', module: 'IoT',
    amount: 64_000, probability: 1.0, expectedCloseDate: iso(60), owner: 'rep-demo-001',
    closeReason: 'cold_chain_proof_point', competitor: null,
    createdAt: iso(95),
  },
  {
    id: 'deal-closed-3', tenantId: T, accountId: 'acct-midwest', accountName: 'Midwest Grocers', contactId: 'contact-jessica-park',
    name: 'Midwest — initial scope', stage: 'closed_lost', module: 'TMS',
    amount: 120_000, probability: 0, expectedCloseDate: iso(45), owner: 'rep-demo-002',
    closeReason: 'budget_delayed', competitor: 'incumbent_tms',
    createdAt: iso(85),
  },
);

// ── CRM-6 Sarah Chen demo scenario ───────────────────────────────────────
// Seed an extra account + contact + demo send + engagement so the Prospect
// Mirror can be exercised end-to-end without running the full pipeline.
SEED_CRM_ACCOUNTS.push({
  id: 'acct-apex-medical',
  tenantId: T,
  name: 'Apex Medical Supply',
  website: 'apexmedicalsupply.com',
  industry: 'Healthcare Distribution — Hospital Supply',
  hq: { city: 'Indianapolis', state: 'IN' },
  employees: 680,
  annualRevenue: 310_000_000,
  painProfiles: ['healthcare_pharma'],
  primaryModule: 'IoT',
  estimatedFreightSpend: 9_900_000,
  pipelineStage: 'qualified',
  accountOwner: 'rep-demo-001',
  tags: ['healthcare', 'cold-chain'],
  activeLoadsCount: 2,
  healthScore: 71,
  notes: 'CRM-6 demo scenario — Sarah Chen champion. Watching DSCSA compliance pain.',
  createdAt: iso(12),
});
SEED_CRM_CONTACTS.push({
  id: 'contact-sarah-chen',
  tenantId: T,
  accountId: 'acct-apex-medical',
  accountName: 'Apex Medical Supply',
  firstName: 'Sarah',
  lastName: 'Chen',
  fullName: 'Sarah Chen',
  title: 'Director of Supply Chain Operations',
  email: 'schen@apexmedicalsupply.com',
  phone: '+1-317-555-0187',
  linkedinUrl: 'linkedin.com/in/sarahchen-scm',
  painProfiles: ['healthcare_pharma'],
  primaryModule: 'IoT',
  seniority: 'director',
  decisionMakerRole: 'economic_buyer',
  timezone: 'America/Indiana/Indianapolis',
  lastActivityAt: iso(0),
  leadScore: 88,
  tags: ['crm6-demo-scenario', 'champion'],
  notes: 'CRM-6 reference contact for the Sarah Chen scenario.',
  latestSignals: [
    { type: 'active_load',       description: '2 active cold-chain loads tracked on Watchmen network.' },
    { type: 'content_engagement', description: 'Opened the Apex cold-chain case study 3x this week.' },
  ],
  createdAt: iso(8),
});

export const SEED_CRM_DEMO_SENDS = [
  {
    id: 'demo-sarah-chen-1',
    tenantId: T,
    contactId: 'contact-sarah-chen',
    accountId: 'acct-apex-medical',
    sequenceId: null,
    responseRoute: 'HIGH_INTEREST',
    moduleKey: 'IoT',
    scenarioId: 'cold-chain-dock-to-broker',
    scenario: {
      module: 'IoT',
      scenarioId: 'cold-chain-dock-to-broker',
      name: 'Cold-Chain Dock-to-Broker Visibility',
      sections: ['Reefer live feed', 'Temp breach alerts', 'Chain-of-custody export', 'Claim dispute workflow'],
      prospectCompany: 'Apex Medical Supply',
      prospectIndustry: 'Healthcare Distribution',
      lane: { origin: 'Indianapolis, IN', destination: 'Phoenix, AZ' },
      comparablePeer: 'Riverside Natural Foods',
      industrySkus: ['Temp-controlled pharmaceuticals', 'Surgical implants'],
      personalized: true,
    },
    demoUrl: 'https://demo.kairos.elyonsci.ai/cold-chain-dock-to-broker?c=contact-sarah-chen',
    providerResult: { mock: true, success: true, provider: 'supademo' },
    sentAt: iso(0),
    openedAt: iso(0),
  },
];

export const SEED_CRM_DEMO_ENGAGEMENT = [
  { id: 'de-sc-1', tenantId: T, demoSendId: 'demo-sarah-chen-1', contactId: 'contact-sarah-chen', event: 'opened', section: null, data: {}, at: iso(0) },
  { id: 'de-sc-2', tenantId: T, demoSendId: 'demo-sarah-chen-1', contactId: 'contact-sarah-chen', event: 'section_time', section: 'Temp breach alerts', data: { seconds: 214 }, at: iso(0) },
  { id: 'de-sc-3', tenantId: T, demoSendId: 'demo-sarah-chen-1', contactId: 'contact-sarah-chen', event: 'section_time', section: 'Chain-of-custody export', data: { seconds: 92 }, at: iso(0) },
];

export const SEED_CRM_PROSPECT_MIRRORS = [
  {
    id: 'contact-sarah-chen',
    tenantId: T,
    contactId: 'contact-sarah-chen',
    accountId: 'acct-apex-medical',
    painProfiles: ['healthcare_pharma'],
    painSignals: [
      { type: 'active_load', description: '2 active cold-chain loads tracked on Watchmen network.' },
      { type: 'content_engagement', description: 'Opened cold-chain case study 3x this week.' },
    ],
    demoSendCount: 1,
    lastDemoSentAt: iso(0),
    hotSections: [{ section: 'Temp breach alerts', seconds: 214 }],
    decisionSignals: ['Demo sent (IoT)', 'Hot section: Temp breach alerts'],
    stakeholders: [{ contactId: 'contact-sarah-chen', name: 'Sarah Chen', title: 'Director of Supply Chain Operations', role: 'economic_buyer' }],
    nextAction: { label: 'Call now — prospect spent 3+ minutes on a key section', priority: 'P0', dueWithinHours: 4 },
    lastRebuiltAt: iso(0),
  },
];

export const CRM_SEED_SUMMARY = {
  crm_accounts: SEED_CRM_ACCOUNTS.length,
  crm_contacts: SEED_CRM_CONTACTS.length,
  crm_deals: SEED_CRM_DEALS.length,
  crm_activities: SEED_CRM_ACTIVITIES.length,
  crm_demo_sends: SEED_CRM_DEMO_SENDS.length,
  crm_demo_engagement: SEED_CRM_DEMO_ENGAGEMENT.length,
  crm_prospect_mirrors: SEED_CRM_PROSPECT_MIRRORS.length,
};
